import type { ReactElement } from "react";
import { Chip, Tooltip } from "@mui/material";

const LABELS: Record<string, string> = {
  active: "Active",
  invited: "Invited",
  blocked: "Blocked",
  deleted: "Removed",
  // SB6-ADM-USERS-01 — distinct label for cross-tenant collaborators.
  external: "External",
};

const COLORS: Record<
  string,
  "success" | "info" | "warning" | "default" | "secondary"
> = {
  active: "success",
  invited: "info",
  blocked: "warning",
  deleted: "default",
  external: "secondary",
};

export interface StatusChipProps {
  status: "active" | "invited" | "blocked" | "deleted" | "external";
  /** Optional tenant label for tooltip — surfaced on "external" rows. */
  tenantName?: string | null;
}

/**
 * Status chip — text-coloured, no fill, no border. The text colour
 * carries the semantic per UX brief §3.2 — Active green, Invited blue,
 * Blocked amber, Removed muted. SB6-ADM-USERS-01 adds an External
 * variant with a home-tenant tooltip.
 */
export function StatusChip({
  status,
  tenantName,
}: StatusChipProps): ReactElement {
  const chip = (
    <Chip
      label={LABELS[status] ?? status}
      size="small"
      color={COLORS[status] ?? "default"}
      variant="outlined"
      aria-label={
        status === "external" && tenantName
          ? `External: ${tenantName}`
          : `Status: ${LABELS[status] ?? status}`
      }
      sx={{
        border: "none",
        "& .MuiChip-label": { px: 0.5 },
      }}
    />
  );
  if (status === "external" && tenantName) {
    return (
      <Tooltip title={`Home tenant: ${tenantName}`} placement="top">
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
}
